/**
 * Palette presets a partner instance can wear instead of Pulse's own.
 *
 * "ledger" is Clerk's green — forest #1C5A43, deep green #11392B, cream
 * #F6F4ED, orange #C46A2B — so a partner running Clerk and Pulse side by side
 * gets one look. The app shell takes it from the token block in index.css
 * (`:root[data-preset='ledger']`); the landing page, whose CSS names Pulse's
 * colours directly, is recoloured by swapping each for its Ledger equivalent.
 */

const PRESET_KEY = 'pulse-preset';

/** Pulse colour → Ledger colour, as hex and as the bare channels rgba() uses. */
const LEDGER: [string, string][] = [
  ['#0E7CFF', '#1C5A43'], ['14,124,255', '28,90,67'], // blue → forest
  ['#0B2A6B', '#11392B'], ['11,42,107', '17,57,43'], // blue ink → deep green
  ['#08205A', '#0B2A20'],
  ['#8AD4FF', '#A9CBB8'], // sky → sage
  ['#FF6A3D', '#C46A2B'], ['255,106,61', '196,106,43'], // coral → orange
  ['#C3421A', '#9A4E1D'],
  ['#C9F24B', '#E7B87A'], ['201,242,75', '231,184,122'], ['155,199,43', '214,160,100'], // lime → warm highlight
  ['#4E6B00', '#8A4A1C'], ['#5C8000', '#8A4A1C'],
  ['#8A6EFF', '#5E8C74'],
  ['#0F1A33', '#11392B'], ['15,26,51', '17,57,43'], // ink
  ['#55617F', '#4C6359'], ['#8A93AC', '#7F8F86'],
  ['#F5F7FC', '#F6F4ED'], ['#FBFCFE', '#FAF8F2'], ['#F6F8FC', '#F3F0E6'], ['#F7F9FD', '#F6F4ED'], // paper → cream
  ['#FFEFE6', '#EFEBDF'], ['#FFE0D0', '#E4DCC7'], ['#FFF7F2', '#F8F5EC'], ['#FFE2D2', '#E9E2CF'], ['#FFD9C4', '#E1D6BC'],
  ['255,239,230', '239,235,223'],
];

/** Recolour CSS (or a single colour) written in Pulse's palette. */
export function themed(css: string, preset: string | undefined): string {
  if (preset !== 'ledger') return css;
  return LEDGER.reduce((out, [from, to]) => out.split(from).join(to).split(from.toLowerCase()).join(to), css);
}

/** Put the preset on <html> for the token block, and remember it so the next
 *  visit paints in the right colours before the partner config arrives. */
export function applyPreset(preset: string | undefined): void {
  const root = document.documentElement;
  if (preset) root.dataset.preset = preset;
  else delete root.dataset.preset;
  try {
    if (preset) localStorage.setItem(PRESET_KEY, preset);
    else localStorage.removeItem(PRESET_KEY);
  } catch {
    // Storage blocked: the preset still applies, it just isn't remembered.
  }
}

/** Called once before React mounts. */
export function restorePreset(): void {
  try {
    const saved = localStorage.getItem(PRESET_KEY);
    if (saved) document.documentElement.dataset.preset = saved;
  } catch {
    // Storage blocked: Pulse's own palette until the config lands.
  }
}
